import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Subscription } from '@adornis/baseql/decorators.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { catchError, from, of, switchMap } from 'rxjs';
import type { Product } from '../_cms/products/entities/Product.js';
import { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { ContextPrefix } from '../permissions.js';
import { SemanticRelationship } from './SemanticRelationship.js';

/**
 * @entity StudentParticipationRelationship
 */
@Entity()
export class StudentParticipationRelationship extends SemanticRelationship {
  public static override _class = 'StudentParticipationRelationship';

  @DataLoadedField(() => Student, ({ subjectID }) => subjectID)
  public override subject: Maybe<Student>;

  @DataLoadedField(() => KPF, ({ objectID }) => objectID)
  public override object: Maybe<KPF<Product>>;

  public override getContext() {
    return this.objectID === GLOBAL_CONTEXT ? GLOBAL_CONTEXT : ContextPrefix.KPF + this.objectID;
  }

  @Subscription(() => [StudentParticipationRelationship])
  static subscribeStudentParticipationRelationshipsForKPFs(@Arg('kpfIDs', () => [String]) kpfIDs: string[]) {
    return (gqlFields: BaseQLSelectionSet<StudentParticipationRelationship>) => {
      const selector = { _class: this._class, objectID: { $in: kpfIDs } };
      const handle = getCollectionHandle<StudentParticipationRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as StudentParticipationRelationship[]);
        }),
      );
    };
  }

  @Subscription(() => [StudentParticipationRelationship])
  static subscribeStudentParticipationRelationshipsForUser(@Arg('userID', () => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<StudentParticipationRelationship>) => {
      const selector = { _class: this._class, subjectID: userID };
      const handle = getCollectionHandle<StudentParticipationRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as StudentParticipationRelationship[]);
        }),
      );
    };
  }
}
